import React from 'react';
import { Box, Chip } from '@mui/material';
import { Warning as WarningIcon, Science as ScienceIcon } from '@mui/icons-material';
import { colors } from '../../../themes/typography';
import { experimentColors } from '../../../themes/experimentColors';

const MachineStrip = ({ machines, selectedMachineId, onSelectMachine }) => {
  const scrollRef = React.useRef(null);
  const selectedRef = React.useRef(null);

  // Auto-scroll to selected chip
  React.useEffect(() => {
    if (selectedRef.current && scrollRef.current) {
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedMachineId]);

  if (!machines || machines.length === 0) return null;

  return (
    <Box
      ref={scrollRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        overflowX: 'auto',
        py: 0.5,
        px: 1,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: colors.border.medium,
          borderRadius: 2
        }
      }}
    >
      {machines.map((machine) => {
        const slots = machine.slots || [];
        const filled = slots.filter(s => s.name && s.d_product_id);
        const criticalCount = filled.filter(s => s.problem === 'Critical').length;
        const experimentCount = filled.filter(s => s.experiment).length;
        const isSelected = machine.machineId === selectedMachineId;

        // Health color for dot
        const healthColor = criticalCount > 0
          ? colors.critical.text
          : experimentCount > 0
            ? experimentColors.running
            : colors.status.success;

        return (
          <Chip
            key={machine.machineId}
            ref={isSelected ? selectedRef : null}
            label={machine.location}
            size="small"
            variant={isSelected ? 'filled' : 'outlined'}
            color={isSelected ? 'primary' : 'default'}
            onClick={() => onSelectMachine(machine.machineId)}
            icon={
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: healthColor,
                ml: '8px !important'
              }} />
            }
            deleteIcon={
              criticalCount > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', mr: '4px !important' }}>
                  <WarningIcon sx={{ fontSize: 12, color: isSelected ? 'rgba(255,255,255,0.8)' : colors.critical.text }} />
                </Box>
              ) : experimentCount > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', mr: '4px !important' }}>
                  <ScienceIcon sx={{ fontSize: 12, color: isSelected ? 'rgba(255,255,255,0.8)' : experimentColors.running }} />
                </Box>
              ) : undefined
            }
            onDelete={criticalCount > 0 || experimentCount > 0 ? () => onSelectMachine(machine.machineId) : undefined}
            sx={{
              height: 28,
              fontSize: '12px',
              fontWeight: isSelected ? 600 : 400,
              flexShrink: 0,
              cursor: 'pointer',
              transition: 'all 150ms ease-out',
              '& .MuiChip-deleteIcon': {
                ml: '-4px'
              },
              ...(!isSelected && {
                '&:hover': {
                  borderColor: colors.primary.main,
                  backgroundColor: 'rgba(59, 130, 246, 0.04)'
                }
              })
            }}
          />
        );
      })}
    </Box>
  );
};

export default React.memo(MachineStrip);
